import {
    AVERAGE_CHART_COLOR,
    AVERAGE_LABEL_COLOR,
    shouldShowAverages,
    SUPERCATEGORY_CONFIG,
} from '@/components/cashflow/charts/chartHelpers';
import { getTooltipDate } from '@/components/cashflow/helpers';
import { TooltipParams } from '@/components/common/charts/chartHelper';
import {
    ChartTooltip,
    ChartTooltipRow,
} from '@/components/common/charts/ChartTooltip';
import { toDollars } from '@/util/currency';
import { CashflowAmount, TransactionsChartDatum } from '@common/react/cashflow';
import { TickUnit } from '@common/valueSeries';
import { app } from '@common/zapatos';
import React, { Fragment, MouseEventHandler } from 'react';

interface SupercategoryTooltipProps {
    tooltip: TooltipParams<TransactionsChartDatum>;
    today: string;
    selectedSupercategories: app.supercategory[];
    primaryKey: Exclude<keyof CashflowAmount, `${string}Avg`>;
    averageKey: Extract<keyof CashflowAmount, `${string}Avg`>;
    onMouseEnter: MouseEventHandler<HTMLDivElement>;
    unit: TickUnit;
    showAverageSpending?: boolean;
}

export const SupercategoryTooltip = ({
    tooltip,
    today,
    selectedSupercategories,
    primaryKey,
    averageKey,
    onMouseEnter,
    unit,
    showAverageSpending = false,
}: SupercategoryTooltipProps) => {
    if (!tooltip.data || !tooltip.visible) return null;

    const rowsVisible =
        tooltip.data.date <= today ||
        shouldShowAverages(selectedSupercategories) ||
        showAverageSpending;

    if (!rowsVisible) return null;

    return (
        <ChartTooltip
            w={220}
            top={0}
            left={tooltip.left}
            title={getTooltipDate(tooltip.data.date, unit)}
            onMouseEnter={onMouseEnter}
        >
            {tooltip.data.date <= today &&
                selectedSupercategories.map((supercategory) => (
                    <Fragment key={supercategory}>
                        <ChartTooltipRow
                            label={SUPERCATEGORY_CONFIG[supercategory].label}
                            labelShape={
                                SUPERCATEGORY_CONFIG[supercategory].shape
                            }
                            labelColor={
                                SUPERCATEGORY_CONFIG[supercategory].color
                            }
                            value={toDollars(
                                tooltip.data![supercategory][primaryKey],
                                {
                                    withCents: false,
                                    alwaysSigned: true,
                                }
                            )}
                        />
                        {showAverageSpending &&
                            supercategory === 'SPENDING' && (
                                // Ensure average spending row is rendered right after spending row
                                <AverageSpendingRow
                                    amount={
                                        tooltip.data!.SPENDING.cumulativeAvg
                                    }
                                />
                            )}
                    </Fragment>
                ))}

            {tooltip.data.date > today && showAverageSpending && (
                // Handle case where tooltip is over a future date
                <AverageSpendingRow
                    amount={tooltip.data!.SPENDING.cumulativeAvg}
                />
            )}

            {shouldShowAverages(selectedSupercategories) && (
                <ChartTooltipRow
                    label="3 month average"
                    labelColor={AVERAGE_CHART_COLOR}
                    textColor={AVERAGE_LABEL_COLOR}
                    value={toDollars(
                        tooltip.data[selectedSupercategories[0]][averageKey],
                        {
                            withCents: false,
                            alwaysSigned: true,
                        }
                    )}
                />
            )}
        </ChartTooltip>
    );
};

const AverageSpendingRow = ({ amount }: { amount: number }) => (
    <ChartTooltipRow
        key="spendingAvg"
        label="Spending (3 mo avg)"
        labelShape={SUPERCATEGORY_CONFIG.SPENDING.shape}
        labelColor={AVERAGE_LABEL_COLOR}
        value={toDollars(amount, {
            withCents: false,
            alwaysSigned: true,
        })}
    />
);
export default SupercategoryTooltip;
